import React from 'react';
import styled, { x, css } from '@xstyled/styled-components';
import { Item, registry, RenderProps } from '../Item';
import { FBPOST } from '../itemIds';
import { Flex } from '~/ui/flex/Flex';
import { Card } from '~/ui/card/Card';
import IconVerified from './icons/IconVerified.svg';
import IconVerifiedDark from './icons/IconVerifiedDark.svg';
import IconMap from './icons/IconMap.svg';
import IconMapDark from './icons/IconMapDark.svg';
import IconLike from './icons/IconLike.svg';
import IconLikeDark from './icons/IconLikeDark.svg';
import IconComment from './icons/IconComment.svg';
import IconCommentDark from './icons/IconCommentDark.svg';
import IconShare from './icons/IconShare.svg';
import IconShareDark from './icons/IconShareDark.svg';
import { t } from 'ttag';
import { useEditorStore } from '~/shell/Store';
import { handlePaste } from '../helper/eventForContentEditable';
import useIsDarkMode from '~/lib/themes/hooks/useIsDarkMode';
import { decodeHtmlEntities } from '~/lib/decodeHtmlEntities';
import { escapeHtml } from '~/lib/sanitization';
import { optionalFieldWrites } from '../text/optionalFieldWrites';
import { useTemplateTextFields } from '../../utils/templateTextFields';

interface Props {
  avatar?: string;
  pic?: string;
  name?: string;
  nameEditable?: boolean;
  title?: string;
  body?: string;
  /**
   * In the editor: whether the source has its own name and title. Learners see
   * 'Username' and 'Title' for empty ones, and an author can write those words.
   */
  hasAuthoredName?: boolean;
  hasAuthoredTitle?: boolean;
}

const FB_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";

const PostCard = styled(Card)<{ $isDark?: boolean }>`
  font-family: ${FB_FONT};
  overflow: hidden;
  border: 0.655px solid ${p => (p.$isDark ? '#a9a9a9' : '#d8dadd')};
  background-color: ${p => (p.$isDark ? '#2e2e2e' : '#ffffff')};
  box-shadow:
    3px 0 8px rgba(0, 11, 50, 0.08),
    0 36.137px 36.137px rgba(29, 0, 91, 0.1),
    0 13.551px 18.068px rgba(50, 32, 64, 0.05);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.img<{ $isDark?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
  box-sizing: border-box;
  background-color: ${p => (p.$isDark ? '#fff' : 'transparent')};
`;

const AuthorMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const AuthorNameRow = styled.div.attrs({ className: 'userText' })`
  display: flex;
  align-items: center;
  gap: 2.6px;
`;

const AuthorName = styled.span<{ $isDark?: boolean }>`
  font-family: ${FB_FONT};
  font-weight: 600;
  font-size: 14px;
  line-height: normal;
  letter-spacing: -0.28px;
  color: ${p => (p.$isDark ? '#ffffff' : '#000000')};
  white-space: nowrap;

  &[contenteditable='true'] {
    position: relative;
    border: 2px solid transparent;
    border-radius: 4px;
    outline: none;

    &:focus:after {
      opacity: 0;
    }

    &:hover,
    &:focus {
      border: 2px solid #9738ff;
    }
  }
`;

const verifiedIconStyles = css`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: block;
`;

const VerifiedIconLight = styled(IconVerified)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: block;
`;

const VerifiedIconDark = styled(IconVerifiedDark)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: block;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const TimeText = styled.span<{ $isDark?: boolean }>`
  font-family: ${FB_FONT};
  font-weight: 400;
  font-size: 14px;
  line-height: normal;
  letter-spacing: -0.07px;
  color: ${p => (p.$isDark ? '#f1f1f1' : '#65676b')};
  white-space: nowrap;
`;

const Dot = styled.span<{ $isDark?: boolean }>`
  font-family: ${FB_FONT};
  font-weight: 400;
  font-size: 10px;
  line-height: normal;
  letter-spacing: -0.05px;
  color: ${p => (p.$isDark ? '#f1f1f1' : '#65676b')};
`;

const mapIconStyles = css`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: block;
`;

const MapIconLight = styled(IconMap)`
  ${mapIconStyles}
`;

const MapIconDark = styled(IconMapDark)`
  ${mapIconStyles}
`;

const BodyBlock = styled.div<{ $isDark?: boolean }>`
  font-family: ${FB_FONT};
  font-weight: 400;
  font-size: 14px;
  line-height: normal;
  letter-spacing: -0.182px;
  color: ${p => (p.$isDark ? '#ffffff' : '#000000')};
  width: 100%;
  box-sizing: border-box;

  &[contenteditable='true'] {
    position: relative;
    border: 2px solid transparent;

    span {
      max-width: 360px;
    }
    &:focus:after {
      opacity: 0;
    }
    &:hover {
      border: 2px solid #9738ff;
    }
  }
`;

const TitleBlock = styled.div<{ $isDark?: boolean }>`
  background-color: ${p => (p.$isDark ? '#6f6f6f' : '#f0f2f5')};
  padding: 8px 16px;
  width: 100%;
  box-sizing: border-box;

  b {
    display: block;
    font-family: ${FB_FONT};
    font-style: normal;
    font-weight: 700;
    font-size: 11.131px;
    line-height: 1.246;
    letter-spacing: -0.3562px;
    color: ${p => (p.$isDark ? '#ffffff' : '#000000')};

    &[contenteditable='true'] {
      position: relative;
      border: 2px solid transparent;

      &:focus:after {
        opacity: 0;
      }

      &:hover {
        border: 2px solid #9738ff;
      }
    }
  }
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding-bottom: 16px;
  box-sizing: border-box;
`;

const EngagementPad = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
  width: 100%;
  box-sizing: border-box;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
`;

const ReactionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2.6px;
`;

const ReactionIcons = styled.div`
  display: flex;
  align-items: center;
`;

const ReactionIcon = styled.span<{
  $from: string;
  $to: string;
  $isDark?: boolean;
  $overlap?: boolean;
}>`
  width: 11.786px;
  height: 11.786px;
  border-radius: 50%;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 1.31px solid ${p => (p.$isDark ? '#2e2e2e' : '#ffffff')};
  background: linear-gradient(180deg, ${p => p.$from} 0%, ${p => p.$to} 100%);
  ${p =>
    p.$overlap &&
    css`
      margin-left: -3px;
    `}
`;

const ReactionCount = styled.span<{ $isDark?: boolean }>`
  font-family: ${FB_FONT};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.246;
  letter-spacing: -0.462px;
  color: ${p => (p.$isDark ? '#f1f1f1' : '#65676b')};
  white-space: nowrap;
`;

const Counts = styled.div<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 3.9px;
  font-family: ${FB_FONT};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.246;
  letter-spacing: -0.462px;
  color: ${p => (p.$isDark ? '#f1f1f1' : '#65676b')};
  white-space: nowrap;
`;

const Separator = styled.div<{ $isDark?: boolean }>`
  width: 100%;
  height: 1px;
  background-color: ${p => (p.$isDark ? '#6f6f6f' : '#e5e5e5')};
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 4px 0;
  box-sizing: border-box;
`;

const actionIconStyles = css`
  width: 18.16px;
  height: 16.736px;
  flex-shrink: 0;
  display: block;
`;

const LikeIconLight = styled(IconLike)`
  ${actionIconStyles}
`;
const LikeIconDark = styled(IconLikeDark)`
  ${actionIconStyles}
`;
const CommentIconLight = styled(IconComment)`
  ${actionIconStyles}
`;
const CommentIconDark = styled(IconCommentDark)`
  ${actionIconStyles}
`;
const ShareIconLight = styled(IconShare)`
  ${actionIconStyles}
`;
const ShareIconDark = styled(IconShareDark)`
  ${actionIconStyles}
`;

const ActionLabel = styled.span<{ $isDark?: boolean }>`
  font-family: ${FB_FONT};
  font-weight: 700;
  font-size: 12px;
  line-height: 1.246;
  color: ${p => (p.$isDark ? '#f1f1f1' : '#65676b')};
`;

/** What learners see for an empty link title or post text (see SocialMedia8). */
const LEARNER_DEFAULTS = { title: 'Title', body: 'Body' };
type DefaultedField = keyof typeof LEARNER_DEFAULTS;

export class Fbpost extends Item<typeof FBPOST, Props> {
  protected render({
    avatar,
    pic,
    name,
    nameEditable = false,
    hasAuthoredName,
    hasAuthoredTitle,
    title,
    body,
    id
  }: RenderProps<Props>) {
    // Only an editor view the author may edit edits the post (not a
    // read-only one).
    const { contentEditable, updateSequence } = useEditorStore();
    const templateTextFields = useTemplateTextFields();
    const canEditName = contentEditable && nameEditable;
    const authoredName =
      (hasAuthoredName ?? (Boolean(name) && name !== 'Username'))
        ? name || ''
        : '';
    // The server stores the name as HTML (a bare "&" as "&amp;"), so the
    // author's name is shown and compared decoded once, and saved escaped. A
    // company's handle, which is not the author's to edit, shows as it comes.
    const typedName = decodeHtmlEntities(authoredName);
    const shownName = nameEditable ? decodeHtmlEntities(name ?? '') : name;
    const authored = {
      title:
        hasAuthoredTitle ??
        (Boolean(title) && title !== LEARNER_DEFAULTS.title),
      // An authored body comes wrapped in markup, so it never reads 'Body'.
      body: Boolean(body) && body !== LEARNER_DEFAULTS.body
    };
    // Each authored part's saves: one at a time, in order, and a refused one
    // is tried again when the author leaves the part again (see
    // optionalFieldWrites). They outlive this post, which a redraw replaces.
    const nameWrites = React.useMemo(
      () =>
        optionalFieldWrites(id, 'subtitle', Boolean(authoredName), typedName),
      [id]
    );
    const partWrites = React.useMemo(
      () => ({
        title: optionalFieldWrites(id, 'title', authored.title),
        body: optionalFieldWrites(id, 'body', authored.body)
      }),
      [id]
    );
    React.useEffect(() => {
      nameWrites.seen(Boolean(authoredName), typedName);
      partWrites.title.seen(authored.title);
      partWrites.body.seen(authored.body);
    }, [
      nameWrites,
      partWrites,
      authoredName,
      typedName,
      authored.title,
      authored.body
    ]);
    // Whether the name, link title and post text show learners' defaults
    // rather than authored text (display only).
    const showsDefault = React.useRef<Record<DefaultedField, boolean>>({
      title: !authored.title,
      body: !authored.body
    });
    const showsDefaultName = React.useRef(!authoredName);
    const decodedTitle = title ? decodeHtmlEntities(title) : '';
    const isDarkMode = useIsDarkMode();

    const handleBlur = (
      fieldType: string,
      event: React.FocusEvent<HTMLElement>
    ) => {
      if (fieldType === 'subtitle') {
        const username = event.currentTarget.textContent?.trim() || '';
        event.currentTarget.textContent = username || 'Username';
        showsDefaultName.current = !username;
        // Names are saved trimmed: one stored with a space round it is the
        // same name, and left as it is, it is not saved again.
        if (username !== (nameWrites.latestValue() ?? '').trim()) {
          nameWrites.save(
            Boolean(username),
            // Sent escaped, so the server's sanitizer keeps it as typed.
            () => updateSequence(id, fieldType, escapeHtml(username)),
            username
          );
        }
        return;
      }

      let newText = event.target.innerHTML;
      newText = newText.replace(/<div\s*><br\s*><\/div\s*>/gi, '<br>');

      let cleanText = newText.replace(/<div><br><\/div>/g, '<br>');
      cleanText = cleanText.replace(/<div>/g, '<br>');
      cleanText = cleanText.replace(/<\/div><br>/g, '<br>');
      cleanText = cleanText.replace(/<div>/g, '\n').replace(/<\/div>/g, '');

      if (!newText.startsWith('<div>')) {
        const firstDivIndex = cleanText.indexOf('\n');
        if (firstDivIndex !== -1) {
          cleanText =
            cleanText.slice(0, firstDivIndex) +
            '\n' +
            cleanText.slice(firstDivIndex + 1);
        }
      }

      // Without the company's template text fields, the link title and post
      // text save as before: an emptied one as 'Type Here'.
      if (!templateTextFields) {
        if (cleanText.trim() == '') {
          cleanText = 'Type Here';
          event.target.innerHTML = cleanText;
        }
        updateSequence(id, fieldType, cleanText);
        return;
      }

      const field = fieldType as DefaultedField;
      const writes = partWrites[field];
      if (cleanText.trim() == '') {
        // Emptied, it shows learners' default again and is saved empty,
        // unless the server already holds no text for it.
        event.target.innerHTML = LEARNER_DEFAULTS[field];
        showsDefault.current[field] = true;
        if (writes.needsClear()) {
          writes.save(false, () => updateSequence(id, fieldType, ''));
        }
        return;
      }

      showsDefault.current[field] = false;
      writes.save(true, () => updateSequence(id, fieldType, cleanText));
    };

    const handleInput = (event: React.FormEvent<HTMLElement>) => {
      let newText = event.currentTarget.textContent;

      if (newText?.trim() == '') {
        newText = ' ';
        event.currentTarget.textContent = newText;

        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(event.currentTarget);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    };

    const handleFocus = (
      fieldType: DefaultedField,
      event: React.FocusEvent<HTMLElement>
    ) => {
      const text = event.currentTarget.textContent || '';
      if (
        contentEditable &&
        ((templateTextFields &&
          showsDefault.current[fieldType] &&
          text === LEARNER_DEFAULTS[fieldType]) ||
          text.toLowerCase() === 'type here')
      ) {
        const element = event.currentTarget;
        element.textContent = '';
        element.focus();

        setTimeout(() => {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(element);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }, 0);
      }
    };

    const VerifiedIcon = isDarkMode ? VerifiedIconDark : VerifiedIconLight;
    const MapIcon = isDarkMode ? MapIconDark : MapIconLight;
    const LikeIcon = isDarkMode ? LikeIconDark : LikeIconLight;
    const CommentIcon = isDarkMode ? CommentIconDark : CommentIconLight;
    const ShareIcon = isDarkMode ? ShareIconDark : ShareIconLight;

    return (
      <Flex variant="center" w="100%" minWidth={0}>
        <PostCard
          $isDark={isDarkMode}
          backgroundColor={isDarkMode ? '#2e2e2e' : 'white'}
          // CAT-122x / WCAG 1.4.10: fill parent up to design width (359/400), never overflow 320 CSS px
          w="100%"
          maxW={{ _: '359px', sm: '400px' }}
          p="0px"
          display="flex"
          flexDirection="column"
          position="relative"
          borderRadius="16px"
          boxSizing="border-box"
        >
          <Header>
            <HeaderContent>
              <AuthorRow>
                {avatar && (
                  <Avatar
                    $isDark={isDarkMode}
                    src={avatar}
                    alt=""
                    data-test-id="fbpost-avatar"
                  />
                )}
                <AuthorMeta>
                  <AuthorNameRow>
                    <AuthorName
                      $isDark={isDarkMode}
                      data-id={`${id}_subtitle`}
                      contentEditable={canEditName}
                      onBlur={
                        canEditName ? e => handleBlur('subtitle', e) : undefined
                      }
                      onFocus={
                        canEditName
                          ? e => {
                              const text = e.currentTarget.textContent || '';
                              const isPlaceholder =
                                showsDefaultName.current && text === 'Username';
                              if (isPlaceholder) {
                                const element = e.currentTarget;
                                element.textContent = '';
                                setTimeout(() => {
                                  const range = document.createRange();
                                  const selection = window.getSelection();
                                  range.selectNodeContents(element);
                                  range.collapse(false);
                                  selection?.removeAllRanges();
                                  selection?.addRange(range);
                                }, 0);
                              }
                            }
                          : undefined
                      }
                      onInput={canEditName ? handleInput : undefined}
                      onPaste={canEditName ? handlePaste : undefined}
                      suppressContentEditableWarning={true}
                    >
                      {shownName}
                    </AuthorName>
                    <VerifiedIcon aria-hidden="true" />
                  </AuthorNameRow>
                  <TimeRow>
                    <TimeText $isDark={isDarkMode}>{t`2h ago`}</TimeText>
                    <Dot $isDark={isDarkMode} aria-hidden="true">
                      •
                    </Dot>
                    <MapIcon aria-hidden="true" />
                  </TimeRow>
                </AuthorMeta>
              </AuthorRow>
              <BodyBlock
                $isDark={isDarkMode}
                contentEditable={contentEditable}
                dangerouslySetInnerHTML={{ __html: body || '' }}
                onBlur={
                  contentEditable ? e => handleBlur('body', e) : undefined
                }
                onInput={contentEditable ? handleInput : undefined}
                onPaste={contentEditable ? handlePaste : undefined}
                onFocus={
                  contentEditable ? e => handleFocus('body', e) : undefined
                }
                suppressContentEditableWarning={true}
              />
            </HeaderContent>
          </Header>

          {pic && <x.img src={pic} alt={t`picture`} w="100%" display="block" />}

          <BottomSection>
            <TitleBlock $isDark={isDarkMode}>
              <b
                contentEditable={contentEditable}
                onBlur={
                  contentEditable ? e => handleBlur('title', e) : undefined
                }
                onInput={contentEditable ? handleInput : undefined}
                onPaste={contentEditable ? handlePaste : undefined}
                onFocus={
                  contentEditable ? e => handleFocus('title', e) : undefined
                }
                suppressContentEditableWarning={true}
              >
                {decodedTitle}
              </b>
            </TitleBlock>

            <EngagementPad aria-hidden="true">
              <StatsRow>
                <ReactionsGroup>
                  <ReactionIcons aria-hidden="true">
                    <ReactionIcon
                      $from="#17adfd"
                      $to="#016edf"
                      $isDark={isDarkMode}
                    />
                    <ReactionIcon
                      $from="#ea6874"
                      $to="#d3373e"
                      $isDark={isDarkMode}
                      $overlap
                    />
                    <ReactionIcon
                      $from="#f6dd79"
                      $to="#eba94f"
                      $isDark={isDarkMode}
                      $overlap
                    />
                  </ReactionIcons>
                  <ReactionCount $isDark={isDarkMode}>3</ReactionCount>
                </ReactionsGroup>
                <Counts $isDark={isDarkMode}>
                  <span>{t`20 Comments`}</span>
                  <span>{t`8 Shares`}</span>
                </Counts>
              </StatsRow>

              <Separator $isDark={isDarkMode} />

              <ActionsRow>
                <Action>
                  <LikeIcon aria-hidden="true" />
                  <ActionLabel $isDark={isDarkMode}>{t`Like`}</ActionLabel>
                </Action>
                <Action>
                  <CommentIcon aria-hidden="true" />
                  <ActionLabel $isDark={isDarkMode}>{t`Comment`}</ActionLabel>
                </Action>
                <Action>
                  <ShareIcon aria-hidden="true" />
                  <ActionLabel $isDark={isDarkMode}>{t`Share`}</ActionLabel>
                </Action>
              </ActionsRow>
            </EngagementPad>
          </BottomSection>
        </PostCard>
      </Flex>
    );
  }
}

registry[FBPOST] = Fbpost;
